/* eslint-disable react-refresh/only-export-components */
import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useLang } from '@/lib/LanguageContext';
import { ta } from '@/lib/i18n';
import { t } from '@/lib/translations';
import {
  Modal,
  ModalContent,
  ModalTitle,
  ModalDescription
} from '@/components/ui/modal';

const STORAGE_KEY = 'oceanne-cookie-consent';

type ConsentValue = 'accepted' | 'rejected' | 'customized';

export interface ConsentCategories {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
}

export interface ConsentPayload {
  value: ConsentValue;
  categories: ConsentCategories;
  timestamp: string;
}

interface CookieBannerProps {
  className?: string;
  onConsent?: (value: ConsentValue) => void;
}

/**
 * Reads consent from localStorage. Supports backward-compat with the old
 * format (plain string "accepted" | "rejected" | "customized") AND the new
 * granular JSON format. Returns null if nothing stored or malformed.
 */
export function getCookieConsent(): ConsentPayload | null {
  if (typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) return null;
  // Legacy string format
  if (raw === 'accepted') {
    return {
      value: 'accepted',
      categories: { essential: true, analytics: true, marketing: true },
      timestamp: ''
    };
  }
  if (raw === 'rejected') {
    return {
      value: 'rejected',
      categories: { essential: true, analytics: false, marketing: false },
      timestamp: ''
    };
  }
  if (raw === 'customized') {
    // Legacy "customized" without categories info — conservative default
    return {
      value: 'customized',
      categories: { essential: true, analytics: false, marketing: false },
      timestamp: ''
    };
  }
  // New JSON format
  try {
    const parsed = JSON.parse(raw) as ConsentPayload;
    if (
      parsed &&
      typeof parsed === 'object' &&
      (parsed.value === 'accepted' || parsed.value === 'rejected' || parsed.value === 'customized') &&
      parsed.categories &&
      typeof parsed.categories.essential === 'boolean' &&
      typeof parsed.categories.analytics === 'boolean' &&
      typeof parsed.categories.marketing === 'boolean'
    ) {
      return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

function persistConsent(
  value: ConsentValue,
  categories: ConsentCategories,
  onConsent?: (value: ConsentValue) => void
) {
  if (typeof window === 'undefined') return;
  const payload: ConsentPayload = {
    value,
    categories,
    timestamp: new Date().toISOString()
  };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(
    new CustomEvent('cookie-consent-change', { detail: { value, categories } })
  );
  onConsent?.(value);
}

export function CookieBanner({ className, onConsent }: CookieBannerProps) {
  const { lang } = useLang();
  const text = ta(t.cookies, lang);
  const [stored, setStored] = useState<ConsentPayload | null>(() => getCookieConsent());
  const [modalOpen, setModalOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  if (stored !== null) return null;

  const handleAccept = () => {
    const cats: ConsentCategories = { essential: true, analytics: true, marketing: true };
    persistConsent('accepted', cats, onConsent);
    setStored({ value: 'accepted', categories: cats, timestamp: new Date().toISOString() });
  };

  const handleReject = () => {
    const cats: ConsentCategories = { essential: true, analytics: false, marketing: false };
    persistConsent('rejected', cats, onConsent);
    setStored({ value: 'rejected', categories: cats, timestamp: new Date().toISOString() });
  };

  const handleSavePreferences = () => {
    const cats: ConsentCategories = { essential: true, analytics, marketing };
    persistConsent('customized', cats, onConsent);
    setStored({ value: 'customized', categories: cats, timestamp: new Date().toISOString() });
    setModalOpen(false);
  };

  return (
    <>
      <div
        role="region"
        aria-label={text.aria_label}
        className={cn(
          // Phase 27.72B — Codex audit fix : bottom-0 partout. MobileStickyBar +
          // SmsFAB sont maintenant hidden tant que pas de choix cookies (cf
          // mobile-sticky-bar.tsx + sms-fab.tsx). Plus besoin de l'offset
          // bottom-[68px] mobile pour éviter chevauchement. Bas écran propre.
          'fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background px-6 py-5 shadow-lg',
          // safe-area-inset-bottom pour iOS notch/home indicator
          'pb-[max(1.25rem,env(safe-area-inset-bottom))]',
          'max-h-[60vh] overflow-y-auto',
          className
        )}
      >
        <div className="mx-auto max-w-5xl flex flex-col md:flex-row md:items-center gap-4">
          <p className="text-sm text-foreground flex-1">
            {text.intro}{' '}
            <a href="/confidentialite" className="underline text-primary">
              {text.policy_link}
            </a>
          </p>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setModalOpen(true)}
              className="rounded-md border border-border px-4 py-2 text-sm font-bold uppercase tracking-widest hover:bg-muted"
            >
              {text.customize}
            </button>
            <button
              type="button"
              onClick={handleReject}
              className="rounded-md border border-border px-4 py-2 text-sm font-bold uppercase tracking-widest hover:bg-muted"
            >
              {text.reject}
            </button>
            <button
              type="button"
              onClick={handleAccept}
              className="rounded-md bg-primary px-4 py-2 text-sm font-bold uppercase tracking-widest text-primary-foreground hover:bg-primary/90"
            >
              {text.accept}
            </button>
          </div>
        </div>
      </div>
      <Modal open={modalOpen} onOpenChange={setModalOpen}>
        <ModalContent className="max-w-2xl">
          <ModalTitle>{text.modal_title}</ModalTitle>
          <ModalDescription>{text.modal_intro}</ModalDescription>
          <div className="mt-4 flex flex-col gap-4">
            <label className="flex gap-3 items-start cursor-not-allowed opacity-70">
              <input
                type="checkbox"
                checked
                disabled
                aria-label={text.cat_essential_title}
                className="mt-1"
              />
              <div>
                <div className="font-bold text-sm">{text.cat_essential_title}</div>
                <div className="text-xs text-muted-foreground">{text.cat_essential_body}</div>
              </div>
            </label>
            <label className="flex gap-3 items-start cursor-pointer">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                aria-label={text.cat_analytics_title}
                className="mt-1"
              />
              <div>
                <div className="font-bold text-sm">{text.cat_analytics_title}</div>
                <div className="text-xs text-muted-foreground">{text.cat_analytics_body}</div>
              </div>
            </label>
            <label className="flex gap-3 items-start cursor-pointer">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                aria-label={text.cat_marketing_title}
                className="mt-1"
              />
              <div>
                <div className="font-bold text-sm">{text.cat_marketing_title}</div>
                <div className="text-xs text-muted-foreground">{text.cat_marketing_body}</div>
              </div>
            </label>
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button
              type="button"
              onClick={handleSavePreferences}
              className="rounded-md bg-primary px-4 py-2 text-sm font-bold uppercase tracking-widest text-primary-foreground hover:bg-primary/90"
            >
              {text.save_preferences}
            </button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
}
